// ─────────────────────────────────────────────────────────────────────
// WHICH PATH NAMES WHICH SURFACE — a LEAF module, and that is the point.
//
// These predicates lived in the route COMPONENTS, which made a cycle:
//
//   SuperadminRoute.tsx  →  app/surface.ts  (for navigateToSurface)
//   app/surface.ts       →  SuperadminRoute.tsx  (for isSuperadminRoutePath)
//
// It compiled and it ran. What it cost was the dev loop: Vite cannot apply a
// hot update inside a circular import, so it gave up and reloaded the whole
// page on every edit —
//
//   [hmr] …SuperadminRoute.tsx failed to apply HMR as it's within a circular
//         import. Reloading page to reset the execution order.
//
// — and a full reload ends the session, because the club token lives in memory
// on purpose (`lib/auth/clubSession.ts`). So every code change signed the
// developer out. The remedy is not to store the token; it is to stop reloading.
//
// This module therefore imports NOTHING. A path predicate is a string question
// with no view attached, and keeping it next to the component that happens to
// render that path is what tied routing to rendering in the first place.
// ─────────────────────────────────────────────────────────────────────

const normalize = (pathname: string) => (pathname || '').replace(/\/+$/, '') || '/';

/** Stable path for the internal panel. Trailing slashes ignored. */
export const SUPERADMIN_ROUTE_PATHS = ['/sa'];

/** True when `pathname` is the internal control panel route. */
export function isSuperadminRoutePath(pathname: string): boolean {
  return SUPERADMIN_ROUTE_PATHS.includes(normalize(pathname));
}

/**
 * Stable physical-pad route paths. A real tablet/kiosk is pointed at one of
 * these; the assigned court is NEVER taken from the URL (no `/pad/:courtId`,
 * no `?courtId=`). Authority is the device token only.
 */
export const BANEPAD_DEVICE_ROUTE_PATHS = ['/pad', '/banepad/device'];

/** True when `pathname` is the physical-pad route (trailing slashes ignored). */
export function isBanepadDeviceRoutePath(pathname: string): boolean {
  return BANEPAD_DEVICE_ROUTE_PATHS.includes(normalize(pathname));
}

/**
 * The WebAuthn diagnostics page (#676). A MEASUREMENT surface: not linked from
 * anywhere, behind no login, and reached in the member app only by the
 * non-production `passkey-probe` EAS build, which starts on it
 * (apps/mobile `PASSKEY_PROBE_PATH` — the two must agree).
 */
export const PASSKEY_PROBE_ROUTE_PATHS = ['/diagnostics/passkeys'];

export function isPasskeyProbeRoutePath(pathname: string): boolean {
  return PASSKEY_PROBE_ROUTE_PATHS.includes(normalize(pathname));
}
